import type { ReactNode } from 'react';
import type { Simulation, SimulationKind } from '@app/shared';
import { formatIsoDate } from '@/lib/format';
import { useSimulations } from './queries';

/** Simulations déjà enregistrées d'un genre donné — lecture seule, pour mémoire. */
export function SavedSimulations<K extends SimulationKind>({
  kind,
  renderHeadline,
}: {
  kind: K;
  renderHeadline: (simulation: Extract<Simulation, { kind: K }>) => ReactNode;
}) {
  const simulations = useSimulations();
  const rows = (simulations.data ?? []).filter(
    (simulation): simulation is Extract<Simulation, { kind: K }> => simulation.kind === kind,
  );

  if (simulations.isPending) return null;
  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-sm font-medium text-muted-foreground">Simulations enregistrées</h2>
      <ul className="flex flex-col gap-1.5">
        {rows.map((simulation) => (
          <li
            key={simulation.id}
            className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-md border border-border px-3 py-2 text-sm"
          >
            <span className="font-medium">
              {simulation.label}
              {simulation.engineVersion === 'mmi-2026-corrected' && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">(corrigé)</span>
              )}
            </span>
            <span className="text-muted-foreground">{formatIsoDate(simulation.createdAt.slice(0, 10))}</span>
            <span>{renderHeadline(simulation)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
